import { DEFAULT_NEXT_TRANSMISSION_CYCLE_DAYS } from "static/constants";
import formatDate from "utils/formatDate";
import {
  processBillingSchedule,
  processDeviceInformation,
  processInitialReportTab,
  processPatientInformation,
  processSecondaryDeviceInformation,
  updateDeviceInformationWithRPM,
  updateProfileValue,
} from "utils/patientProfileState";

export const transmissionPatientModalAction = {
  changePatient: (state, action) => {
    const { patientId, serialNumber, account } = action.payload;

    state.patientSerialNumber = serialNumber;
    state.patientId = patientId;
    state.patientAccount = account;
  },

  changePatientTab: (state, action) => {
    state.patientTab = action.payload;
  },

  changeProfileTab: (state, action) => {
    state.profileTab = action.payload;
  },

  setScrollToNotesSection: (state, action) => {
    state.scrollToNotesSection = action.payload;
  },

  setAccessToProfileTabs: (state, action) => {
    state.hasAccessToProfileTabs = action.payload;
  },

  loadPatient: (state, action) => {
    const patient = action.payload;
    if (patient && Object.entries(patient).length > 0) {
      Object.entries(patient).forEach(([key, value]) => {
        switch (key) {
          case "insurance":
            state.originalInsurance = value;
            break;

          case "secondaryInsurance":
            state.originalSecondaryInsurance = value;
            break;

          case "tertiaryInsurance":
            state.originalTertiaryInsurance = value;
            break;

          case "guarantor":
            state.guarantor = value;
            state.originalGuarantor = value;
            break;

          case "information":
            processPatientInformation(value, state);
            break;

          case "deviceInformation":
            processDeviceInformation({
              deviceInfo: value,
              key: "deviceInformation",
              originalKey: "originalDeviceInformation",
              state,
            });
            break;

          case "secondary_device_information":
            processSecondaryDeviceInformation(value, state);
            break;

          case "patientAlertInformation":
            state.patientAlertInformation = value;
            state.originalPatientAlertInformation = value;
            break;

          case "initial_report_tab":
            processInitialReportTab(value, state);
            break;

          case "alertBanner":
            state[key] = value?.bannerText;
            state.originalAlertBanner = value?.bannerText;
            break;

          case "implanted_billing_schedule":
            processBillingSchedule(value, state);
            break;

          case "rpm_devices":
            updateDeviceInformationWithRPM(value, state);
            break;

          case "next_send_cycle":
            updateProfileValue({
              key: "nextSendDays",
              value: value ?? DEFAULT_NEXT_TRANSMISSION_CYCLE_DAYS,
              state,
            });
            break;

          case "next_bill_date":
            updateProfileValue({
              key: "next_bill_date",
              value: formatDate(value),
              state,
            });
            break;

          case "next_send":
            updateProfileValue({
              key: "next_send",
              value: formatDate(value),
              state,
            });
            break;

          default:
            updateProfileValue({
              key,
              value,
              state,
            });
            break;
        }
      });
    }
  },
  setBenchmarkPatientInsurances: (state, action) => {
    state.benchmarkInsurances = action.payload;
  },
  changeProfileField: (state, action) => {
    const { type, value } = action.payload;

    switch (type) {
      case "information": {
        state.information = value;
        break;
      }

      case "originalInformation": {
        state.originalInformation = value;
        break;
      }

      case "patientHistoryInformation": {
        state.patientHistoryInformation = value;
        break;
      }

      case "patientOriginalHistoryInformation": {
        state.patientOriginalHistoryInformation = value;
        break;
      }

      case "deviceInformation": {
        state.deviceInformation = { ...state.deviceInformation, ...value };
        break;
      }

      case "secondaryDeviceInformation": {
        state.secondaryDeviceInformation = value;
        break;
      }

      case "originalSecondaryDeviceInformation": {
        state.originalSecondaryDeviceInformation = value;
        break;
      }

      case "originalDeviceInformation": {
        state.originalDeviceInformation = {
          ...state.originalDeviceInformation,
          ...value,
        };
        break;
      }

      case "originalInsurance": {
        state.originalInsurance = value;
        break;
      }

      case "originalSecondaryInsurance": {
        state.originalSecondaryInsurance = value;
        break;
      }

      case "originalTertiaryInsurance": {
        state.originalTertiaryInsurance = value;
        break;
      }

      case "patientAlertInformation": {
        state.patientAlertInformation = value;
        break;
      }
      case "billingSchedule": {
        state.billingSchedule = value;
        break;
      }
      case "originalBillingSchedule": {
        state.originalBillingSchedule = value;
        break;
      }
      case "alertBanner": {
        state.alertBanner = value;
        break;
      }
      case "originalAlertBanner": {
        state.originalAlertBanner = value;
        break;
      }

      case "originalProfile": {
        state.originalProfile = value;
        break;
      }

      case "originalPatientAlertInformation": {
        state.originalPatientAlertInformation = value;
        break;
      }

      default: {
        state.profile = value;
      }
    }
  },

  resetOriginalProfile: (state, action) => {
    state.originalProfile = {};
  },

  setBillingEqual: (state, action) => {
    state.isBillingEqual = action.payload;
  },

  loadPatientReports: (state, action) => {
    state.patientReports = action.payload;
  },

  setPatientCurrentTask: (state, action) => {
    const { _id, description, status, account, notes } = action.payload;

    state.currentPatientTask = {
      _id,
      account,
      description,
      status: status !== "" ? status : "To Do",
      notes,
    };
  },

  setFetchPatientTasks: (state, action) => {
    state.fetchPatientTasks = action.payload;
  },

  toggleDataPointMode: (state, action) => {
    state.isDataPointUpdated = action.payload;
  },

  setFetchPatientReports: (state, action) => {
    state.fetchPatientReports = action.payload;
  },

  toggleDeactivate: (state, action) => {
    const { deactivateFrom, isDeactivate, reason } = action.payload;
    state.isDeactivate = isDeactivate;
    state.deactivateFrom = deactivateFrom;

    if (!isDeactivate && !reason) {
      state.profile = state.originalProfile;
    }
  },

  setPhoneOptOutInformation: (state, action) => {
    const { primaryNumberOptOut, secondaryNumberOptOut, emergencyPhoneOptOut } =
      action.payload;

    state.phoneOptOutInformation = {
      primaryNumberOptOut,
      secondaryNumberOptOut,
      emergencyPhoneOptOut,
    };
  },

  setUpdatedPatientNotes: (state, action) => {
    const { note } = action.payload;
    state.patientCommunicationAndNotes.updatedNotes = note;
  },
};
